import React, { useEffect, useRef } from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Colors, authorRole } from "../../utils/constants";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { MathJax } from "better-react-mathjax";
import Text from "@mui/material/Typography";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  getDDRConfirmationCall,
  getDDRFormDetailsCall,
  getDDRRequestLibraryListCall,
  submitDDRCall,
} from "../../api/ddrFormApi";
import { getUserTypes } from "../../api/Profile.api";
import { notify } from "../../utils/Notify";
import { modalBaseStyles } from "../modals/RootModal";
import objectToQueryString from "../../screens/browseJournal/objToQuery";
import {
  goToTop,
  isStringOfArray,
  showIsProfileToast,
  sortAuthorDetail,
} from "../../utils/helper";
import metaData from "../../utils/metadata";
import {
  addToQueue,
  clearQueue,
  removeFromQueue,
} from "../../store/slice/ddrQueueSlice";
import Disclaimer from "./disclaimer";
import IdIcon from "../../assets/img/id.png";
import { AuthorFinderObj } from "../../types/interface";
import {
  clearSearchTerm,
  updatePage,
  updateRow,
} from "../../store/slice/searchSlice";
import {
  clearAuthorSearchTerm,
  updateAuthorPage,
  updateAuthorRow,
} from "../../store/slice/authorFinderSlice";
import {
  clearAllFilters,
  clearAllFiltersValue,
  clearPublicationFilter,
  updateAllFilter,
  updateAuthor,
  updateResearcher,
  updateSpeaker,
} from "../../store/slice/filterSlice";
import { clearQuery } from "../../store/slice/queryForCountSlice";
import { onAuthorResult as authorsearch } from "../../screens/AuthorFinder/onAuthorResult";
import { onSearch as search } from "../../screens/Search/onSearch";

// ─── Types ────────────────────────────────────────────────────────────────────

type QueueFormInputs = {
  firstName: string;
  lastName: string;
  email: string;
  college: string;
  department: string;
  profileType: string;
  rollNo: string;
  mobileNum: string;
  regionalStation: string;
  city: string;
  pin: string;
  message: string;
  admin: string;
  otherProfile: string;
};

interface ToDetails {
  ddrRequestToList: { label: string; value: string }[];
  selectedDdrRequestTo: string;
}

// ─── ArticleCard ──────────────────────────────────────────────────────────────

interface ArticleCardProps {
  article: any;
  authorReducer: any;
  fromJournalToc: boolean;
  onAuthorClick: (authorName: string, article: any) => void;
  showDelete: boolean;
  onDelete: (articleId: string) => void;
}

function ArticleCard({
  article,
  authorReducer,
  fromJournalToc,
  onAuthorClick,
  showDelete,
  onDelete,
}: ArticleCardProps) {
  return (
    <Box
      sx={{
        backgroundColor: Colors.white,
        borderRadius: "16px",
        border: "1px solid #D1D5DB",
        padding: "2%",
        height: "100%",
        position: "relative",
      }}
    >
      {showDelete && (
        <Tooltip title="Remove from queue">
          <IconButton
            size="medium"
            onClick={() => onDelete(article.article_id)}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "error.main",
              "&:hover": { backgroundColor: "error.lighter" },
            }}
          >
            <DeleteOutlineIcon fontSize="medium" />
          </IconButton>
        </Tooltip>
      )}
      <Typography sx={webStyles.heading}>Article Details</Typography>

      <Typography sx={[webStyles.titleText]} gutterBottom>
        Title
      </Typography>
      <MathJax>
        <Typography sx={webStyles.text}>
          <div dangerouslySetInnerHTML={{ __html: article?.title }} />
        </Typography>
      </MathJax>

      <Typography sx={webStyles.titleText}>Authors</Typography>
      {article?.author_details?.length &&
      !isStringOfArray(article?.author_details) ? (
        <Box sx={{ display: "flex", marginTop: "5px" }} flexWrap="wrap">
          {sortAuthorDetail(article?.author_details)?.map(
            (obj: any, i: number) => {
              const firstName = obj?.author_fname ? obj?.author_fname : "";
              const lastName = obj?.author_lname ? obj?.author_lname : "";

              const reversedName = lastName
                ? lastName + ", " + firstName
                : firstName;
              const displayName = lastName
                ? lastName.replace(/,/g, " ") +
                  ", " +
                  firstName.replace(/,/g, " ")
                : firstName.replace(/,/g, " ");

              return (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Tooltip title={authorRole[obj?.datauthorrole_id]}>
                    <Button
                      sx={{
                        "&:hover": {
                          background: authorReducer.searchTerm
                            ? !authorReducer.searchTerm?.includes(reversedName)
                              ? "transparent"
                              : "#E4DCFE"
                            : "transparent",
                          textDecoration: "underline",
                        },
                        background: authorReducer.searchTerm
                          ? !authorReducer.searchTerm?.includes(reversedName)
                            ? ""
                            : "#E4DCFE"
                          : "",
                        padding: 0,
                        textDecoration: "underline",
                        minWidth: 0,
                        paddingLeft: i === 0 ? 0 : 1,
                        paddingRight: 1,
                        color: Colors.primary,
                      }}
                      onClick={() => onAuthorClick(reversedName, article)}
                    >
                      {displayName.length ? displayName : ""}{" "}
                    </Button>
                  </Tooltip>
                  {obj?.orc_id ? (
                    <a
                      href={`https://orcid.org/${obj?.orc_id}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img
                        src={IdIcon}
                        style={{
                          paddingLeft: "5px",
                          marginTop: "8px",
                          paddingRight: "6px",
                        }}
                        alt="IdIcon"
                      />
                    </a>
                  ) : null}
                  <Divider
                    orientation="vertical"
                    style={{ height: "60%", alignSelf: "center" }}
                  />
                </Box>
              );
            },
          )}
        </Box>
      ) : null}

      <Text
        component={"div"}
        sx={{
          textAlign: "left",
          fontSize: "16px",
          color: Colors.primary,
          fontStyle: "italic",
          cursor: "pointer",
          marginBlock: "12px",
        }}
      >
        {metaData(article)}
      </Text>
    </Box>
  );
}

// ─── ArticleRow (Article Card + its own To Details) ───────────────────────────

interface ArticleRowProps {
  article: any;
  toDetails: ToDetails;
  onChange: (articleId: string, field: keyof ToDetails, value: any) => void;
  onDelete: (articleId: string) => void;
  showDelete: boolean;
  authorReducer: any;
  fromJournalToc: boolean;
  onAuthorClick: (authorName: string, article: any) => void;
}

function ArticleRow({
  article,
  toDetails,
  onChange,
  onDelete,
  showDelete,
  authorReducer,
  fromJournalToc,
  onAuthorClick,
}: ArticleRowProps) {
  const { ddrRequestToList, selectedDdrRequestTo } = toDetails;
  const [ddrSelectOpen, setDdrSelectOpen] = React.useState(false);

  React.useEffect(() => {
    if (!ddrSelectOpen) return;
    const handleScroll = () => setDdrSelectOpen(false);
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [ddrSelectOpen]);

  return (
    <Box
      sx={{
        backgroundColor: Colors.white,
        borderRadius: "16px",
        border: "1px solid #9e9a9c",
        padding: "2%",
        mb: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
        }}
      >
        {/* Left 50%: Article card */}
        <Box sx={{ width: { xs: "100%", md: "70%" } }}>
          <ArticleCard
            article={article}
            authorReducer={authorReducer}
            fromJournalToc={fromJournalToc}
            onAuthorClick={onAuthorClick}
            showDelete={showDelete}
            onDelete={onDelete}
          />
        </Box>

        {/* Right 50%: To Details for this article */}
        <Box
          sx={{
            width: { xs: "100%", md: "30%" },
            backgroundColor: Colors.white,
            borderRadius: "12px",
            border: "1px solid #D1D5DB",
            padding: "2%",
          }}
        >
          <Typography sx={webStyles.heading}>Request To Details</Typography>

          {/* DDR Request To */}
          <Box sx={{ mt: 2 }}>
            <label style={webStyles.inputLabel}>DDR Request To*</label>
            {ddrRequestToList.length ? (
              <Select
                fullWidth
                size="small"
                sx={[webStyles.inputLabel, { mt: 1, mb: 1 }]}
                value={selectedDdrRequestTo}
                onChange={(e) =>
                  onChange(
                    article.article_id,
                    "selectedDdrRequestTo",
                    e.target.value,
                  )
                }
                open={ddrSelectOpen}
                onOpen={() => setDdrSelectOpen(true)}
                onClose={() => setDdrSelectOpen(false)}
                displayEmpty
                MenuProps={{
                  disableScrollLock: true,
                  PaperProps: { style: { maxHeight: "40vh" } },
                }}
              >
                {ddrRequestToList.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <Typography
                      sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                    >
                      {opt.label}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            ) : null}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ─── DdrQueueScreen ───────────────────────────────────────────────────────────

function DdrQueueScreen() {
  const location = useLocation();
  const currentArticle = location?.state?.data;

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const queue = useAppSelector((state: any) => state.ddrQueue.queue);

  const authorReducer: any = useSelector(
    (state: AuthorFinderObj) => state.authorReducer,
  );

  const fromJournalToc =
    location?.state?.resultFrom ===
      "BrowseJournalTableSearch/BrowseJournalSearch" ||
    location?.pathname.includes("BrowseJournalTableSearch/BrowseJournalSearch");

  const infotnaticsCustomerId = sessionStorage.getItem(
    "informaticscustomer_id",
  );
  const userId = sessionStorage.getItem("user_id");

  // Per-article To Details map: { [article_id]: ToDetails }
  const [toDetailsMap, setToDetailsMap] = React.useState<
    Record<string, ToDetails>
  >({});

  // Shared Article Delivery To (shown above Cc Details)
  const [articleDeliveryToList, setArticleDeliveryToList] = React.useState<
    { label: string; value: string }[]
  >([]);
  const [selectedArticleDeliveryTo, setSelectedArticleDeliveryTo] =
    React.useState<string>("");

  // Shared form state
  const [iilcheck, setIilcheck] = React.useState(true);
  const [userTypesList, setUserTypesList] = React.useState<any[]>([]);
  const [profileType, setProfileType] = React.useState<any>("Student");
  const [regionalStation, setRegionalStation] = React.useState("No");
  const [regionalInstitute, setRegionalInstitute] = React.useState("");
  const [ddrRequestForm, setDdrRequestForm] = React.useState<any>();
  const [duplicateData, setDuplicateData] = React.useState(false);
  const [submitFormData, setSubmitFormData] = React.useState<any>();
  const [disclaimerDisplay, setDisclaimerDisplay] = React.useState(false);

  const clgName = useSelector(
    (state: any) => state?.customer?.customerDetails?.customername,
  );
  const customerIds = useSelector(
    (state: any) => state?.customer?.customerDetails?.informaticscustomer_id,
  );
  const adminEmail = useSelector(
    (state: any) => state?.customer?.customerDetails?.librarian_emailid,
  );
  const customerId = useSelector((state: any) => state.login);
  const personalDetails = useAppSelector(
    (state) => state.customer.personalDetails,
  );
  const userDetails = useAppSelector((state) => state.customer.customerDetails);

  const profileStatus = sessionStorage.getItem("isProfileUser");
  const isProfileUser = profileStatus === "true";
  const disableInputField =
    profileStatus === "false" && userDetails?.allow_profile === false;

  const { register, control, handleSubmit, setValue, reset } =
    useForm<QueueFormInputs>();

  // Track which article IDs we have already fetched To Details for
  const fetchedIds = useRef<Set<string>>(new Set());

  // Ensure current article is in queue on mount
  useEffect(() => {
    if (currentArticle) {
      dispatch(addToQueue(currentArticle));
    }
    goToTop();
  }, []);

  // Load To Details for every article in the queue that hasn't been loaded yet
  useEffect(() => {
    queue.forEach((article: any) => {
      const id: string = article.article_id;
      if (fetchedIds.current.has(id)) return;
      fetchedIds.current.add(id);
      loadToDetailsForArticle(article);
    });
  }, [queue]);

  const loadToDetailsForArticle = async (article: any) => {
    const id: string = article.article_id;

    // Pre-populate with empty defaults immediately so the row renders
    setToDetailsMap((prev) => ({
      ...prev,
      [id]: prev[id] ?? {
        ddrRequestToList: [],
        selectedDdrRequestTo: "",
      },
    }));

    const response = await getDDRRequestLibraryListCall(
      "?" +
        objectToQueryString({
          customerId: infotnaticsCustomerId,
          article_id: id,
        }),
    );

    if (response?.data?.message !== "Ok") return;

    const reqList = response?.data?.data[0][0]?.customer_list ?? [];
    const ddrRequestToList = Array.isArray(reqList)
      ? reqList.map((obj: any) => ({
          label: String(Object.values(obj)[0]),
          value: String(Object.keys(obj)[0]),
        }))
      : [];

    const randomIndex = ddrRequestToList.length
      ? Math.floor(Math.random() * ddrRequestToList.length)
      : 0;
    const defaultDdrRequestTo = ddrRequestToList[randomIndex]?.value ?? "";

    const apiData = {
      UserMasterId: userId,
      customerId: infotnaticsCustomerId,
      article_id: id,
      RequestToCustomerId: defaultDdrRequestTo,
    };

    const res = await getDDRFormDetailsCall("?" + objectToQueryString(apiData));
    const userData: any = res?.data?.data[0][0];

    // For the first / current article, pre-fill the shared form fields
    // if (id === currentArticle?.article_id) {
    setDdrRequestForm(userData);
    setValue("college", clgName ?? "");
    setValue("department", userData?.department ?? "");
    setValue("mobileNum", userData?.contact_no ?? "");
    setValue("email", userData?.user_email_id ?? "");
    // setValue("admin", userData?.cc_mail_id ?? adminEmail ?? "");
    setValue("admin", userData?.consortia_name ?? "");
    setValue("firstName", personalDetails?.firstname ?? "");
    setValue("lastName", personalDetails?.lastname ?? "");
    // }

    const deliveryList: { label: string; value: string }[] = (
      userData?.article_deliver_to ?? []
    ).map((el: any) => ({
      label: el.customername,
      value: String(el.informaticsconsortiamember_id),
    }));

    // Populate shared delivery list once (from the first article loaded)
    setArticleDeliveryToList((prev) =>
      prev.length === 0 ? deliveryList : prev,
    );
    setSelectedArticleDeliveryTo((prev) =>
      prev === "" ? (customerIds ?? "") : prev,
    );

    setToDetailsMap((prev) => ({
      ...prev,
      [id]: {
        ddrRequestToList,
        selectedDdrRequestTo: defaultDdrRequestTo,
      },
    }));
  };

  // Load user types once
  useEffect(() => {
    getUserTypes().then((types) => setUserTypesList(types ?? []));
  }, []);

  // Update a single field in toDetailsMap for one article
  const handleToDetailsChange = (
    articleId: string,
    field: keyof ToDetails,
    value: any,
  ) => {
    setToDetailsMap((prev) => ({
      ...prev,
      [articleId]: { ...prev[articleId], [field]: value },
    }));
  };

  // ── Submission helpers ──────────────────────────────────────────────────────

  const buildItems = (formData: QueueFormInputs, isDuplicate = false) =>
    queue.map((article: any) => {
      const id: string = article.article_id;
      const td = toDetailsMap[id];

      const item: any = {
        requestingcustomer_id: selectedArticleDeliveryTo,
        deliveringcustomer_id: td?.selectedDdrRequestTo,
        article_id: id,
        requesteduserid: customerId?.user_id,
        requesteduseremailid: formData.email,
        requestremarks: formData.message,
        requester_username: `${formData.firstName} ${formData.lastName}`,
        requester_department: formData.department,
        requester_rollno: formData.rollNo,
        requester_mobile: formData.mobileNum,
        requester_designation: formData.profileType,
        regional_institute: regionalInstitute,
        regional_city: formData.city,
        regional_pin: formData.pin,
        librarian_emailid: formData.admin,
        requester_designation_right: formData.otherProfile || "",
        ...(isDuplicate ? { is_duplicate: true } : {}),
      };
      if (formData.profileType !== "Others") {
        delete item.requester_designation_right;
      }
      return item;
    });

  const handleAuthor = async (authorName: string, article: any) => {
    dispatch(clearSearchTerm());
    dispatch(clearAuthorSearchTerm());
    dispatch(clearAllFilters());
    dispatch(clearPublicationFilter());
    dispatch(clearQuery());
    dispatch(clearAllFiltersValue());
    dispatch(updatePage(1));
    dispatch(updateRow(15));
    dispatch(updateAuthorPage(1));
    dispatch(updateAuthorRow(15));

    if (article?.authors?.length) {
      dispatch(updateAuthor([authorName]));
      dispatch(updateAllFilter({ key: "author", value: authorName }));
    } else if (article?.speakers?.length) {
      dispatch(updateSpeaker([authorName]));
      dispatch(updateAllFilter({ key: "speaker", value: authorName }));
    } else if (article?.researcher_tk === authorName) {
      dispatch(updateResearcher([authorName]));
      dispatch(updateAllFilter({ key: "researcher", value: authorName }));
    }

    if (authorReducer.isAuthorResult === true) {
      dispatch(authorsearch());
      navigate("/authorfindersearch");
    } else {
      dispatch(search());
      navigate("/basicSearchScreen");
    }
  };

  const validate = async (items: any[]) => {
    if (!iilcheck) {
      showIsProfileToast("Enable ILL declaration");
      return;
    }

    const apiFormData = new FormData();
    apiFormData.append("detailsjson", JSON.stringify({ data: items }));

    const response = await submitDDRCall(apiFormData);
    if (response?.data?.message[0][0][0]?.ddr_reference_id) {
      const getcall = await getDDRConfirmationCall(
        "?" +
          objectToQueryString({
            ddr_reference_id: response.data.message[0][0][0].ddr_reference_id,
          }),
      );
      if (
        getcall?.data?.message === "Ok" ||
        getcall?.data?.message[0]?.ddr_reference_id
      ) {
        notify("success", "Request sent successfully");
        dispatch(clearQueue());
        navigate(-1);
      }
    }
  };

  const onSubmit: SubmitHandler<QueueFormInputs> = (formData) => {
    setSubmitFormData(formData);
    if (ddrRequestForm?.duplicate_request) {
      setDuplicateData(true);
    } else {
      validate(buildItems(formData));
      reset({
        firstName: "",
        lastName: "",
        department: "",
        rollNo: "",
        mobileNum: "",
        message: "",
      });
      setIilcheck(false);
    }
  };

  const handleDuplicateConfirm = () => {
    validate(buildItems(submitFormData, true));
    setDuplicateData(false);
    setIilcheck(false);
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* ── Header ── */}
      <Box padding={{ xs: "2% 1%", sm: "1.2% 3%" }} sx={webStyles.flexBox}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackIosNewIcon sx={{ alignSelf: "center" }} fontSize="small" />
        </IconButton>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            // textAlign: "center",
            width: "100%",
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "1.1rem", sm: "1.5rem" },
              fontFamily: { xs: "Helvetica Neue", sm: "Lora" },
              fontWeight: { xs: 900, sm: 700 },
              // alignSelf: "center",
            }}
          >
            Request for Photocopy of an article
          </Typography>
          <Typography
            sx={{
              fontFamily: "Helvetica Neue",
              fontSize: { xs: "0.8rem", sm: "0.9rem" },
              color: Colors.gray_600,
              fontStyle: "italic",
              marginLeft: "2%",
            }}
          >
            Select the institute from the dropdown (Request To Details) to send
            your request
          </Typography>
        </Box>
      </Box>
      <Divider />

      <Box sx={{ padding: "0.5% 3% 0%", backgroundColor: Colors.white }}>
        {/* <Typography
          sx={{
            fontFamily: "Helvetica Neue",
            fontSize: { xs: "0.8rem", sm: "0.9rem" },
            color: Colors.gray_600,
            fontStyle: "italic",
          }}
        >
          Select the institute from the dropdown to send your request
        </Typography> */}
      </Box>

      <Box sx={{ padding: "1% 3%" }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* ── Per-article rows: [Article Card 50%] [To Details 50%] ── */}
          {queue.map((article: any) => {
            const id: string = article.article_id;
            const td: ToDetails = toDetailsMap[id] ?? {
              ddrRequestToList: [],
              selectedDdrRequestTo: "",
            };
            return (
              <ArticleRow
                key={id}
                article={article}
                toDetails={td}
                onChange={handleToDetailsChange}
                onDelete={(articleId) => dispatch(removeFromQueue(articleId))}
                showDelete={queue.length > 1}
                authorReducer={authorReducer}
                fromJournalToc={fromJournalToc}
                onAuthorClick={handleAuthor}
              />
            );
          })}

          {/* ── Shared form fields (From Details + Cc Details) ── */}
          <Box
            sx={{
              backgroundColor: Colors.white,
              borderRadius: "16px",
              border: "1px solid #D1D5DB",
              mt: 1,
            }}
          >
            {/* From Details */}
            <Box
              sx={{
                width: {
                  xs: "100%",
                  sm: "100%",
                  md: "55%",
                  lg: "55%",
                  xl: "55%",
                },
                padding: "1%",
              }}
            >
              <Typography sx={webStyles.heading}>From Details</Typography>

              <Box sx={[webStyles.rowBox, { margin: "3% 0%" }]}>
                <Box width={{ xs: "100%", sm: "49%" }}>
                  <label style={webStyles.inputLabel}>First Name *</label>
                  <TextField
                    {...register("firstName", { required: true })}
                    sx={{ marginTop: "2%" }}
                    inputProps={{
                      style: { height: "1.3rem" },
                      readOnly: isProfileUser,
                    }}
                    variant="outlined"
                    fullWidth
                  />
                </Box>
                <Box width={{ xs: "100%", sm: "49%" }}>
                  <label style={webStyles.inputLabel}>Last Name*</label>
                  <TextField
                    {...register("lastName", { required: true })}
                    sx={{
                      marginTop: "2%",
                      marginLeft: { xs: "0%", sm: "2%" },
                    }}
                    inputProps={{
                      style: { height: "1.3rem" },
                      readOnly: isProfileUser,
                    }}
                    variant="outlined"
                    fullWidth
                  />
                </Box>
              </Box>

              <label style={webStyles.inputLabel}>Email</label>
              <TextField
                {...register("email")}
                sx={{ margin: "2% 0%" }}
                inputProps={{
                  style: { height: "1.3rem" },
                  readOnly: isProfileUser,
                }}
                variant="outlined"
                fullWidth
              />

              <label style={webStyles.inputLabel}>
                College/University/Institution*
              </label>
              <Box
                width={{
                  xs: "100%",
                  sm: "100%",
                  md: "48%",
                  lg: "48%",
                  xl: "48%",
                }}
              >
                <TextField
                  {...register("college", { required: true })}
                  sx={{ margin: "2% 0%" }}
                  inputProps={{
                    style: { height: "1.3rem" },
                    readOnly: isProfileUser,
                  }}
                  variant="outlined"
                  fullWidth
                />
              </Box>

              <label style={webStyles.inputLabel}>Department*</label>
              <Box
                width={{
                  xs: "100%",
                  sm: "100%",
                  md: "48%",
                  lg: "48%",
                  xl: "48%",
                }}
              >
                <TextField
                  {...register("department", { required: true })}
                  sx={{ margin: "2% 0%" }}
                  inputProps={{
                    style: { height: "1.3rem" },
                    readOnly: isProfileUser,
                  }}
                  variant="outlined"
                  fullWidth
                />
              </Box>

              {userTypesList[0] && (
                <Box>
                  <InputLabel style={webStyles.inputLabel}>
                    Type of Profile
                  </InputLabel>
                  <Controller
                    name="profileType"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Select
                        sx={[
                          webStyles.inputLabel,
                          {
                            width: {
                              xs: "100%",
                              sm: "100%",
                              md: "48%",
                              lg: "48%",
                              xl: "48%",
                            },
                            margin: "1% 0%",
                          },
                        ]}
                        onChange={(e) => {
                          if (isProfileUser) return;
                          onChange(e.target.value);
                          setProfileType(e.target.value);
                        }}
                        defaultValue="Student"
                        value={value}
                        inputProps={{ readOnly: isProfileUser }}
                      >
                        {userTypesList.map((e: any, i: number) => (
                          <MenuItem key={i} value={e[i + 1]}>
                            {e[i + 1]}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  {profileType === "Others" && (
                    <Box sx={{ width: "48%" }}>
                      <TextField
                        {...register("otherProfile", { required: true })}
                        sx={{ margin: "2% 0%" }}
                        inputProps={{ style: { height: "1.3rem" } }}
                        variant="outlined"
                        fullWidth
                      />
                    </Box>
                  )}
                </Box>
              )}

              {!disableInputField && profileType === "Student" && (
                <>
                  <label style={webStyles.inputLabel}>Roll No/Admin No*</label>
                  <Box
                    width={{
                      xs: "100%",
                      sm: "100%",
                      md: "48%",
                      lg: "48%",
                      xl: "48%",
                    }}
                  >
                    <TextField
                      {...register("rollNo", { required: true })}
                      sx={{ margin: "2% 0%" }}
                      inputProps={{ style: { height: "1.3rem" } }}
                      variant="outlined"
                      fullWidth
                    />
                  </Box>
                </>
              )}

              <label style={webStyles.inputLabel}>Mobile/Phone No</label>
              <Box
                width={{
                  xs: "100%",
                  sm: "100%",
                  md: "48%",
                  lg: "48%",
                  xl: "48%",
                }}
              >
                <TextField
                  {...register("mobileNum")}
                  sx={{ margin: "2% 0%" }}
                  inputProps={{
                    style: { height: "1.3rem" },
                    readOnly: isProfileUser,
                  }}
                  variant="outlined"
                  fullWidth
                />
              </Box>

              <Box>
                <InputLabel style={webStyles.inputLabel}>
                  If you are from Regional Station/Institution/College
                </InputLabel>
                <Controller
                  name="regionalStation"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Select
                      sx={[
                        webStyles.inputLabel,
                        {
                          width: {
                            xs: "100%",
                            sm: "100%",
                            md: "48%",
                            lg: "48%",
                            xl: "48%",
                          },
                          margin: "1% 0%",
                        },
                      ]}
                      onChange={(e) => {
                        onChange(e.target.value);
                        setRegionalStation(e.target.value);
                        setRegionalInstitute(
                          e.target.value === "Yes" ? "1" : "2",
                        );
                      }}
                      value={regionalStation}
                      displayEmpty
                    >
                      <MenuItem value="No">No</MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                    </Select>
                  )}
                />
              </Box>

              {regionalStation === "Yes" && (
                <>
                  <label style={webStyles.inputLabel}>City</label>
                  <Box
                    width={{
                      xs: "100%",
                      sm: "100%",
                      md: "48%",
                      lg: "48%",
                      xl: "48%",
                    }}
                  >
                    <TextField
                      {...register("city", { required: true })}
                      sx={{ margin: "2% 0%" }}
                      inputProps={{ style: { height: "1.3rem" } }}
                      variant="outlined"
                      fullWidth
                    />
                  </Box>
                  <label style={webStyles.inputLabel}>PIN</label>
                  <Box
                    width={{
                      xs: "100%",
                      sm: "100%",
                      md: "48%",
                      lg: "48%",
                      xl: "48%",
                    }}
                  >
                    <TextField
                      {...register("pin", { required: true })}
                      sx={{ margin: "2% 0%" }}
                      inputProps={{ style: { height: "1.3rem" } }}
                      variant="outlined"
                      fullWidth
                    />
                  </Box>
                </>
              )}

              <label style={webStyles.inputLabel}>Message</label>
              <TextField
                {...register("message")}
                sx={{ margin: "2% 0%" }}
                multiline
                rows={6}
                maxRows={6}
                variant="outlined"
                fullWidth
              />
            </Box>

            {/* To Details */}
            <Box
              sx={{
                width: {
                  xs: "100%",
                  sm: "100%",
                  md: "55%",
                  lg: "55%",
                  xl: "55%",
                },
                padding: "1%",
              }}
            >
              <Typography sx={webStyles.heading}>To Details</Typography>
              <label style={webStyles.inputLabel}>Article Delivery To*</label>
              <Select
                fullWidth
                size="small"
                sx={[webStyles.inputLabel, { mt: 1, mb: 1 }]}
                value={selectedArticleDeliveryTo || customerIds}
                onChange={(e) => setSelectedArticleDeliveryTo(e.target.value)}
                displayEmpty
                MenuProps={{
                  disableScrollLock: true,
                  PaperProps: { style: { maxHeight: "40vh" } },
                }}
              >
                {[
                  { label: clgName, value: customerIds },
                  ...articleDeliveryToList,
                ].map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <Typography
                      sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                    >
                      {opt.label}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {/* Cc Details */}
            <Box
              sx={{
                width: {
                  xs: "100%",
                  sm: "100%",
                  md: "65%",
                  lg: "65%",
                  xl: "65%",
                },
                padding: "1%",
              }}
            >
              <Typography sx={webStyles.heading}>Cc Details</Typography>

              {/* <label style={webStyles.inputLabel}>Library Admin*</label> */}
              <label style={webStyles.inputLabel}>Consortia Admin*</label>
              <Box
                sx={{
                  width: {
                    xs: "100%",
                    sm: "100%",
                    md: "42%",
                    lg: "42%",
                    xl: "42%",
                  },
                }}
              >
                <TextField
                  {...register("admin", { required: true })}
                  inputProps={{
                    style: { height: "1.3rem" },
                    // readOnly: isProfileUser,
                    readOnly: true,
                  }}
                  variant="outlined"
                  fullWidth
                />
              </Box>

              <FormControlLabel
                control={<Checkbox checked={iilcheck} />}
                label="ILL - User Declaration*"
                onChange={() => setIilcheck(!iilcheck)}
              />
            </Box>

            <Typography
              sx={[
                webStyles.inputLabel,
                { padding: "1%", color: Colors.gray_600 },
              ]}
            >
              I need this article for my personal study and research only. I
              agree not to copy, modify, loan, sell, distribute or create
              derivative works based on the content in this article either in
              whole or part. Wherever I use any part of the content of this
              article, I will acknowledge by citing the article.
            </Typography>

            <Button onClick={() => setDisclaimerDisplay(!disclaimerDisplay)}>
              <Typography style={{ color: Colors.primary, padding: "0% 1%" }}>
                Disclaimer
              </Typography>
            </Button>

            <Box sx={[webStyles.rowBox, { padding: "1%" }]}>
              <Button type="submit" variant="contained" sx={webStyles.button}>
                Send
              </Button>
              <Button
                sx={webStyles.button}
                onClick={() => {
                  setIilcheck(false);
                  reset({
                    firstName: "",
                    lastName: "",
                    department: "",
                    rollNo: "",
                    mobileNum: "",
                    message: "",
                  });
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </form>
      </Box>

      {/* ── Duplicate request modal ── */}
      <Modal
        open={duplicateData}
        onClose={() => {}}
        aria-labelledby="DdrQueueDuplicateModal-Title"
      >
        <Box
          sx={{
            ...modalBaseStyles,
            display: "flex",
            flexDirection: "column",
            width: {
              xs: "100%",
              sm: "50vw",
              md: "35vw",
              lg: "35vw",
              xl: "35vw",
            },
            maxHeight: "77vh",
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{ width: "10%", display: "flex", marginLeft: "auto", mb: 2 }}
          >
            <IconButton onClick={() => setDuplicateData(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Text>
            This is a duplicate request. Do you want to make the request again?
          </Text>
          <Box sx={{ width: "100%", my: 2 }}>
            <Button
              variant="contained"
              sx={{ px: "3rem", py: "0.5rem", mr: 2 }}
              onClick={handleDuplicateConfirm}
            >
              OK
            </Button>
            <Button
              variant="outlined"
              onClick={() => setDuplicateData(false)}
              sx={{ px: "3rem", py: "0.5rem", mr: 2 }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>

      <Disclaimer
        handleClickOpen={() => setDisclaimerDisplay(!disclaimerDisplay)}
        disclaimerDisplay={disclaimerDisplay}
      />
    </Box>
  );
}

export default DdrQueueScreen;

// ─── Styles ───────────────────────────────────────────────────────────────────

const webStyles = {
  flexBox: {
    display: "flex",
    flexDirection: "row",
    backgroundColor: Colors.white,
  },
  rowBox: {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    backgroundColor: Colors.white,
  },
  heading: {
    fontFamily: "Helvetica Neue",
    fontWeight: 900,
    fontSize: { xs: "1.1rem", sm: "1.5rem" },
  },
  text: {
    fontFamily: "Helvetica Neue",
    fontWeight: 400,
    fontSize: "1rem",
  },
  titleText: {
    fontWeight: 700,
    fontSize: "0.875rem",
    fontFamily: "Helvetica Neue",
    marginTop: "0.9%",
  },
  inputLabel: {
    fontFamily: "Helvetica Neue",
    fontWeight: 500,
    fontSize: "0.875rem",
  },
  button: {
    padding: { xs: "4% 4%", sm: "0.5% 4%" },
  },
};
